import { useState, useEffect, useRef , type ReactNode, type ChangeEvent as ReactChangeEvent, type SyntheticEvent as ReactSyntheticEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  MenuIcon, InboxIcon, MessageIcon,
  SettingsIcon, PlusIcon, ChevronDownIcon,
  FolderIcon, HashIcon, UserIcon, SparklesIcon, GlobeIcon, CpuIcon,
  ActivityIcon, ClockIcon, BrainIcon, PuzzleIcon, ServerIcon,
  ImageIcon, KeyIcon, LogoutIcon, UsersIcon, ChartIcon, XIcon, CheckIcon,
  PhoneIcon, BellIcon, BellOffIcon, EditIcon, TrashIcon, ExternalLinkIcon,
  BookIcon, NetworkIcon, MegaphoneIcon, SendIcon, XCircleIcon, RefreshIcon,
  FileTextIcon
} from '../icons'
import { useAuth } from '../context/AuthContext'
import { PresenceDot } from './PresenceDot'
import { avatarUrl as getAvatarUrl } from '../utils/avatar'
import { useNotificationContext } from '../context/NotificationContext'
import { TaskNotificationBell } from './TaskNotificationBell'
import { ChatWidgetManager } from './ChatWidget'
import type { ChannelCallState, ChatWidget, KanbanBoard, PublicUser, TopBuyer, Workspace, WorkspaceChannel, WorkspaceMember, User } from '../types'
import { API_URL, getAuthHeaders } from '../api'
import { getToken } from '../api'
import { EditBoardModal } from './EditBoardModal'
import { ProfileModal } from './ProfileModal'
import { EditWorkspaceModal } from './EditWorkspaceModal'
import { EditChannelModal } from './EditChannelModal'
import { EditBoardMembersModal } from './EditBoardMembersModal'
import { BroadcastModal } from './BroadcastModal'

// Format a positive number as compact currency: 2467 -> $2.4k, 1234567 -> $1.2M
function formatCompact(n: number) {
  const sign = n < 0 ? '-' : ''
  const abs = Math.abs(n)
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}k`
  return `${sign}$${abs.toFixed(0)}`
}

const SkullIcon = ({ size = 11, color = '#ff0000' }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path d="M256 32C132.3 32 32 124.8 32 239.3c0 68.5 33.9 129.4 86.8 168.2V464c0 13.3 10.7 24 24 24h48c8.8 0 16-7.2 16-16v-32c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v32c0 8.8 7.2 16 16 16h48c13.3 0 24-10.7 24-24v-56.5c52.9-38.8 86.8-99.7 86.8-168.2C480 124.8 379.7 32 256 32z" fill={color}/>
    <circle cx="168" cy="232" r="40" fill="#0a0a0a"/>
    <circle cx="344" cy="232" r="40" fill="#0a0a0a"/>
    <circle cx="168" cy="232" r="16" fill={color}/>
    <circle cx="344" cy="232" r="16" fill={color}/>
    <path d="M232 320h48l-8 24h-32z" fill="#0a0a0a"/>
    <rect x="220" y="344" width="8" height="20" rx="2" fill="#0a0a0a"/>
    <rect x="240" y="344" width="8" height="20" rx="2" fill="#0a0a0a"/>
    <rect x="260" y="344" width="8" height="20" rx="2" fill="#0a0a0a"/>
    <rect x="280" y="344" width="8" height="20" rx="2" fill="#0a0a0a"/>
  </svg>
)

const mainMenu = [
  { id: 'inbox-channels', name: 'WhatsApp / Telegram', icon: <PhoneIcon size={16} /> },
  { id: 'emails', name: 'Emails', icon: <InboxIcon size={16} /> },
  { id: 'wiki', name: 'Wiki', icon: <BookIcon size={16} /> },
  { id: 'notes', name: 'Notion', icon: <FileTextIcon size={16} /> },
  { id: 'hierarchy', name: 'Hierarquia', icon: <NetworkIcon size={16} /> },
  { id: 'bms', name: 'BMs', icon: <UsersIcon size={16} /> },
]

const spaceList = [
  { id: 'all', name: 'Todas as tarefas', icon: <FolderIcon size={16} /> },
  { id: 'operacional', name: 'Operacional', icon: <FolderIcon size={16} />, count: 5 },
  { id: 'blackorca', name: 'Black Orca - Ops', icon: <FolderIcon size={16} />, count: 16 },
]


function withoutOpenChannel(unread: Record<string, number>) {
  const next = { ...(unread || {}) }
  const match = window.location.pathname.match(/^\/channel\/([0-9a-f-]{36})/)
  if (match) delete next[match[1]]
  return next
}

export function Layout({ children, activeView, onNavigate, topBar, agentPanel, unreadMessages, onMessagesRead }: { children: ReactNode; activeView: string; onNavigate: (path: string) => void; topBar?: ReactNode; agentPanel?: ReactNode; unreadMessages?: number; onMessagesRead?: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, updateUser } = useAuth()
  const { mutedUsers, muteUser, unmuteUser } = useNotificationContext()
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('atlas_sidebar_open') !== 'false')
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [dmUsers, setDmUsers] = useState<PublicUser[]>([])
  const [topBuyersMap, setTopBuyersMap] = useState<Record<string, TopBuyer>>({})
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null)
  const [wsChannels, setWsChannels] = useState<WorkspaceChannel[]>([])
  const [wsBoards, setWsBoards] = useState<KanbanBoard[]>([])
  const [showNewBoard, setShowNewBoard] = useState(false)
  const [newBoardName, setNewBoardName] = useState('')
  const [showNewWorkspace, setShowNewWorkspace] = useState(false)
  const [newWsName, setNewWsName] = useState('')
  const [showNewChannel, setShowNewChannel] = useState(false)
  const [newChannelName, setNewChannelName] = useState('')
  const [showBroadcastModal, setShowBroadcastModal] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [editingStatus, setEditingStatus] = useState(false)
  const [statusDraft, setStatusDraft] = useState('')
  const [channelCalls, setChannelCalls] = useState<Record<string, ChannelCallState>>({}) // { channelId: { participants: [userId...], isVideo } }
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; userId: string; name: string } | null>(null)
  const [viewUserProfile, setViewUserProfile] = useState<PublicUser | null>(null)
  const [deactivatingUser, setDeactivatingUser] = useState(false)
  const [dmUnread, setDmUnread] = useState<Record<string, number>>({})
  const [channelUnread, setChannelUnread] = useState<Record<string, number>>({})
  const [wsContextMenu, setWsContextMenu] = useState<{ ws: Workspace; x: number; y: number } | null>(null)
  const [wsDropdownOpen, setWsDropdownOpen] = useState(false)
  const wsDropdownRef = useRef<HTMLDivElement | null>(null)
  const [editWsModal, setEditWsModal] = useState<Workspace | null>(null)
  const [channelContextMenu, setChannelContextMenu] = useState<{ channel: WorkspaceChannel; x: number; y: number } | null>(null)
  const [boardContextMenu, setBoardContextMenu] = useState<{ board: KanbanBoard; x: number; y: number } | null>(null)
  const [editBoardModal, setEditBoardModal] = useState<KanbanBoard | null>(null)
  const [editBoardMembersModal, setEditBoardMembersModal] = useState<KanbanBoard | null>(null)
  const [editChannelModal, setEditChannelModal] = useState<WorkspaceChannel | null>(null)
  const [multiBoardIds, setMultiBoardIds] = useState(() => {
    try {
      const raw = sessionStorage.getItem('atlas_multi_board_ids')
      if (raw) {
        const ids = JSON.parse(raw)
        if (Array.isArray(ids)) return new Set(ids.map(String))
      }
    } catch { /* ignore */ }
    return new Set<string>()
  })
  const [chatWidgets, setChatWidgets] = useState<ChatWidget[]>([])
  const [unreadEmails, setUnreadEmails] = useState(0)
  const [unreadChannels, setUnreadChannels] = useState(0)
  const [downAccountIds, setDownAccountIds] = useState<string[]>([])
  const [seenDownIds, setSeenDownIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('atlas_bms_seen_down')
      if (raw) {
        const ids = JSON.parse(raw)
        if (Array.isArray(ids)) return new Set(ids.map(String))
      }
    } catch { /* ignore */ }
    return new Set<string>()
  })

  // Fetch down account ids for the sidebar badge + listen for monitor alerts.
  // The badge counts only accounts the user hasn't acknowledged — visiting the
  // BMs page marks the current down set as seen.
  useEffect(() => {
    const persistSeen = (s: Set<string>) => {
      try { localStorage.setItem('atlas_bms_seen_down', JSON.stringify([...s])) } catch { /* ignore */ }
    }
    const loadDownSites = async () => {
      try {
        const res = await fetch(`${API_URL}/api/monitor/down-count`, { headers: getAuthHeaders() })
        if (res.ok) {
          const data = await res.json()
          const ids: string[] = Array.isArray(data.down_ids) ? data.down_ids.map(String) : []
          setDownAccountIds(ids)
          // Drop seen entries that recovered — a later drop should alert again.
          setSeenDownIds(prev => {
            const cur = new Set(ids)
            const next = new Set([...prev].filter(id => cur.has(id)))
            if (next.size !== prev.size) persistSeen(next)
            return next
          })
        }
      } catch { /* ignore */ }
    }
    loadDownSites()
    const interval = setInterval(loadDownSites, 30000)
    const handleSiteNotif = () => loadDownSites()
    window.addEventListener('site-status-changed', handleSiteNotif)
    window.addEventListener('account-status-changed', handleSiteNotif)
    return () => {
      clearInterval(interval)
      window.removeEventListener('site-status-changed', handleSiteNotif)
      window.removeEventListener('account-status-changed', handleSiteNotif)
    }
  }, [])

  // Visiting the BMs page acknowledges the current down set — badge clears.
  useEffect(() => {
    if (!location.pathname.startsWith('/bms') || downAccountIds.length === 0) return
    setSeenDownIds(prev => {
      const next = new Set(prev)
      for (const id of downAccountIds) next.add(id)
      try { localStorage.setItem('atlas_bms_seen_down', JSON.stringify([...next])) } catch { /* ignore */ }
      return next
    })
  }, [location.pathname, downAccountIds])

  // Fetch unread email count
  useEffect(() => {
    const loadUnreadEmails = async () => {
      try {
        const res = await fetch(`${API_URL}/api/email/unread-count`, { headers: getAuthHeaders() })
        if (res.ok) {
          const data = await res.json()
          setUnreadEmails(data.count || 0)
        }
      } catch { /* ignore */ }
    }
    loadUnreadEmails()
    const interval = setInterval(loadUnreadEmails, 30000)
    // Listen for real-time email notifications
    const handleEmailNotif = () => loadUnreadEmails()
    window.addEventListener('email-notification', handleEmailNotif)
    return () => {
      clearInterval(interval)
      window.removeEventListener('email-notification', handleEmailNotif)
    }
  }, [])

  // Fetch unread WhatsApp/Telegram channel count
  useEffect(() => {
    const loadUnreadChannels = async () => {
      try {
        const res = await fetch(`${API_URL}/api/channel/unread-count`, { headers: getAuthHeaders() })
        if (res.ok) {
          const data = await res.json()
          setUnreadChannels(data.count || 0)
        }
      } catch { /* ignore */ }
    }
    loadUnreadChannels()
    const interval = setInterval(loadUnreadChannels, 30000)
    // Listen for real-time channel (WhatsApp/Telegram) message notifications
    const handleChannelNotif = (e: Event) => {
      const notif = (e as CustomEvent).detail
      // Only reload if this is a channel_message (WhatsApp/Telegram) and not from_me
      if (notif?.type === 'channel_message' && !notif?.from_me) {
        loadUnreadChannels()
      }
    }
    window.addEventListener('dm-notification', handleChannelNotif)
    return () => {
      clearInterval(interval)
      window.removeEventListener('dm-notification', handleChannelNotif)
    }
  }, [])

  // Close workspace dropdown when clicking outside
  useEffect(() => {
    if (!wsDropdownOpen) return
    const handleClick = (e: MouseEvent) => {
      if (wsDropdownRef.current && !wsDropdownRef.current.contains(e.target as Node)) {
        setWsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [wsDropdownOpen])

  const openChatWidget = (type: string, id: string, name?: string, avatar?: string, presenceStatus?: string, extra?: { channelId?: string; platform?: string }) => {
    // Close sidebar on mobile so the widget is visible
    if (isMobile) setSidebarOpen(false)
    setChatWidgets(prev => {
      // Replace existing widget if already open (refreshes its data)
      const filtered = prev.filter(w => !(w.type === type && w.id === id))
      const next = [...filtered, { type, id, name, avatar, presenceStatus, ...extra }]
      // Limit to 3 open widgets
      return next.slice(-3)
    })
    // Clear unread badge for this conversation in the sidebar
    if (type === 'dm') {
      setDmUnread(prev => {
        if (!prev[id]) return prev
        const next = { ...prev }
        delete next[id]
        return next
      })
    } else if (type === 'channel') {
      setChannelUnread(prev => {
        if (!prev[id]) return prev
        const next = { ...prev }
        delete next[id]
        return next
      })
    }
    // Re-fetch total unread count from backend so the page title updates.
    // Delay slightly to allow the widget's mark-as-read request to complete
    // first, otherwise fetchUnreadCount would still see the old unread count.
    if (onMessagesRead) setTimeout(onMessagesRead, 500)
  }
  const closeChatWidget = (type: string, id: string) => {
    setChatWidgets(prev => prev.filter(w => !(w.type === type && w.id === id)))
  }

  // Listen for external requests to open a chat widget (e.g. from ChannelInboxView)
  useEffect(() => {
    const handleOpenWidget = (e: Event) => {
      const { type, id, name, avatar, channelId, platform } = (e as CustomEvent).detail || {}
      if (!type || !id) return
      openChatWidget(type, id, name, avatar, undefined, { channelId, platform })
    }
    window.addEventListener('open-chat-widget', handleOpenWidget)
    return () => window.removeEventListener('open-chat-widget', handleOpenWidget)
  }, [])

  // Close mini widget when navigating to the full screen version of same conversation
  useEffect(() => {
    const dmMatch = location.pathname.match(/^\/dm\/([0-9a-f-]{36})/)
    const chMatch = location.pathname.match(/^\/channel\/([0-9a-f-]{36})/)
    if (dmMatch) setChatWidgets(prev => prev.filter(w => !(w.type === 'dm' && w.id === dmMatch[1])))
    if (chMatch) setChatWidgets(prev => prev.filter(w => !(w.type === 'channel' && w.id === chMatch[1])))
  }, [location.pathname])

  // Sync presence status of open DM widgets whenever dmUsers refreshes
  useEffect(() => {
    setChatWidgets(prev => {
      let changed = false
      const next = prev.map(w => {
        if (w.type !== 'dm') return w
        const u = dmUsers.find(u => u.id === w.id)
        const status = u?.presence_status || 'offline'
        if (w.presenceStatus !== status) { changed = true; return { ...w, presenceStatus: status } }
        return w
      })
      return changed ? next : prev
    })
  }, [dmUsers])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const saveStatusMessage = async (text: string) => {
    const token = getToken()
    try {
      const res = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status_message: text }),
      })
      const data = await res.json()
      if (res.ok && updateUser) {
        updateUser({ status_message: data.status_message })
      }
    } catch {}
    setEditingStatus(false)
  }

  // Detect mobile and auto-close sidebar
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768
      setIsMobile(mobile)
      if (mobile) setSidebarOpen(false)
      else setSidebarOpen(localStorage.getItem('atlas_sidebar_open') !== 'false')
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Close sidebar on navigation in mobile
  // (the actual go function is defined below, this just tracks mobile state)

  // Fetch all users for DM list (with presence) + unread counts
  useEffect(() => {
    const loadUsers = () => {
      Promise.all([
        fetch(`${API_URL}/api/users`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API_URL}/api/dm`, { headers: getAuthHeaders() }).then(r => r.json()).catch(() => ({ conversations: [] })),
      ]).then(([usersData, dmData]) => {
        const users = (usersData.users || []).filter((u: PublicUser) => u.id !== user?.id)
        setDmUsers(users)
        // Build unread map from conversations, excluding the currently open DM
        const openDM = location.pathname.match(/^\/dm\/([0-9a-f-]{36})/)
        const openDMId = openDM ? openDM[1] : null
        const unreadMap: Record<string, number> = {}
        ;(dmData.conversations || []).forEach((c: { id: string; unread_count: number }) => {
          if (c.unread_count > 0 && c.id !== openDMId) unreadMap[c.id] = c.unread_count
        })
        setDmUnread(unreadMap)
      }).catch(() => {})
    }
    loadUsers()
    // Refresh every 20s to update presence status and unread counts
    const interval = setInterval(loadUsers, 20000)
    return () => clearInterval(interval)
  }, [user?.id, location.pathname])

  // Fetch top buyers (rank + profit per creator email) for DM sidebar display
  useEffect(() => {
    const companyId = user?.companyId || '69fa3dafac6292f6c9950955'
    const loadTopBuyers = () => {
      fetch(`${API_URL}/api/top-buyers?companyId=${companyId}`, { headers: getAuthHeaders() })
        .then(r => r.json())
        .then(data => {
          const map: Record<string, TopBuyer> = {}
          ;(data.buyers || []).forEach((b: TopBuyer) => {
            map[b.email.toLowerCase()] = b
          })
          setTopBuyersMap(map)
        })
        .catch(() => {})
    }
    loadTopBuyers()
    const interval = setInterval(loadTopBuyers, 60000) // refresh every 60s
    return () => clearInterval(interval)
  }, [user?.companyId])

  // Fetch workspaces
  useEffect(() => {
    fetch(`${API_URL}/api/workspaces`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const ws = data.workspaces || []
        setWorkspaces(ws)
        // Auto-select first workspace or from localStorage
        const savedId = localStorage.getItem('atlas_ws_id')
        const saved = ws.find((w: Workspace) => String(w.id) === savedId)
        if (saved) {
          setSelectedWorkspace(saved)
        } else if (ws.length > 0) {
          const first = ws[0]
          setSelectedWorkspace(first)
          localStorage.setItem('atlas_ws_id', String(first.id))
          window.dispatchEvent(new Event('workspace-changed'))
        }
      })
      .catch(() => {})
  }, [user?.id])

  // Fetch channels and boards when workspace changes
  useEffect(() => {
    if (!selectedWorkspace) { setWsChannels([]); setWsBoards([]); return }
    fetch(`${API_URL}/api/workspaces/${selectedWorkspace.id}/channels`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        setWsChannels(data.channels || [])
        setChannelUnread(withoutOpenChannel(data.unread))
      })
      .catch(() => setWsChannels([]))
    fetch(`${API_URL}/api/workspaces/${selectedWorkspace.id}/boards`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const boards = data.boards || []
        setWsBoards(boards)
        // Clean up multiBoardIds: remove any IDs that no longer exist.
        // In multi mode (/multi/ in the URL or a stored 'multi' board id) with
        // nothing selected, default to all boards so the merged view works.
        const multiActive = sessionStorage.getItem('atlas_board_id') === 'multi' ||
          location.pathname.includes('/multi/')
        setMultiBoardIds(prev => {
          const validIds = new Set(boards.map((b: KanbanBoard) => String(b.id)))
          const cleaned = new Set<string>()
          for (const id of prev) {
            if (validIds.has(id)) cleaned.add(id)
          }
          if (cleaned.size === 0 && multiActive) {
            for (const id of validIds) cleaned.add(String(id))
          }
          if (cleaned.size === prev.size && [...cleaned].every(id => prev.has(id))) return prev
          if (cleaned.size === 0) {
            sessionStorage.removeItem('atlas_multi_board_ids')
            sessionStorage.removeItem('atlas_board_id')
          } else {
            sessionStorage.setItem('atlas_multi_board_ids', JSON.stringify(Array.from(cleaned)))
            if (multiActive) sessionStorage.setItem('atlas_board_id', 'multi')
          }
          window.dispatchEvent(new Event('multi-board-ids-changed'))
          return cleaned
        })
      })
      .catch(() => setWsBoards([]))
  }, [selectedWorkspace])

  // Entering multi mode (/multi/ in the URL) with no board selected defaults
  // to all boards — an explicit subset is preserved, a deliberate "none"
  // selection only survives outside multi URLs.
  useEffect(() => {
    if (!selectedWorkspace || wsBoards.length === 0) return
    if (!location.pathname.includes('/multi/')) return
    setMultiBoardIds(prev => {
      if (prev.size > 0) return prev
      const all = new Set(wsBoards.map(b => String(b.id)))
      sessionStorage.setItem('atlas_multi_board_ids', JSON.stringify(Array.from(all)))
      sessionStorage.setItem('atlas_board_id', 'multi')
      window.dispatchEvent(new Event('multi-board-ids-changed'))
      return all
    })
  }, [location.pathname, selectedWorkspace, wsBoards])

  // Fetch active channel calls for all channels in the selected workspace
  useEffect(() => {
    if (!selectedWorkspace || wsChannels.length === 0) {
      setChannelCalls({})
      return
    }
    const checkCalls = () => {
      wsChannels.forEach(ch => {
        fetch(`${API_URL}/api/group-channel/${ch.id}/call`, { headers: getAuthHeaders() })
          .then(r => r.json())
          .then(data => {
            setChannelCalls(prev => {
              if (data.active) {
                return { ...prev, [ch.id]: data }
              } else {
                const next = { ...prev }
                delete next[ch.id]
                return next
              }
            })
          })
          .catch(() => {})
      })
    }
    checkCalls()
    const interval = setInterval(checkCalls, 15000)
    return () => clearInterval(interval)
  }, [selectedWorkspace, wsChannels])

  // Listen for channel-call-started / updated events
  useEffect(() => {
    const handleCallStarted = (e: Event) => {
      const { channel_id, call_id, is_video, participants } = (e as CustomEvent).detail || {}
      if (channel_id) {
        setChannelCalls(prev => ({
          ...prev,
          [channel_id]: { call_id, is_video, participants: participants || [], active: true }
        }))
      }
    }
    const handleCallUpdated = (e: Event) => {
      const { channel_id, ended } = (e as CustomEvent).detail || {}
      if (!channel_id) return
      if (ended) {
        // Call ended — remove from sidebar
        setChannelCalls(prev => {
          const next = { ...prev }
          delete next[channel_id]
          return next
        })
      } else {
        // Someone joined or left — re-fetch the call info for this channel
        fetch(`${API_URL}/api/group-channel/${channel_id}/call`, { headers: getAuthHeaders() })
          .then(r => r.json())
          .then(data => {
            setChannelCalls(prev => {
              if (data.active) {
                return { ...prev, [channel_id]: data }
              } else {
                const next = { ...prev }
                delete next[channel_id]
                return next
              }
            })
          })
          .catch(() => {})
      }
    }
    window.addEventListener('channel-call-started', handleCallStarted)
    window.addEventListener('channel-call-updated', handleCallUpdated)
    return () => {
      window.removeEventListener('channel-call-started', handleCallStarted)
      window.removeEventListener('channel-call-updated', handleCallUpdated)
    }
  }, [])

  // Listen for real-time DM and channel notifications to update unread badges
  useEffect(() => {
    const handler = (e: Event) => {
      const notif = (e as CustomEvent).detail
      if (notif?.type === 'group_message' && notif?.channel_id) {
        // Don't increment if this channel is currently open (full page or chat widget)
        const openChannel = location.pathname.match(/^\/channel\/([0-9a-f-]{36})/)
        const openChannelId = openChannel ? openChannel[1] : null
        if (openChannelId === notif.channel_id) return
        if (chatWidgets.some(w => w.type === 'channel' && String(w.id) === String(notif.channel_id))) return
        // Use unread_count from backend if present (number, including 0);
        // only fall back to incrementing if it's missing (undefined/null).
        setChannelUnread(prev => {
          const next = { ...prev }
          const uc = notif.unread_count
          if (typeof uc === 'number' && uc > 0) {
            next[notif.channel_id] = uc
          } else if (typeof uc === 'number' && uc === 0) {
            delete next[notif.channel_id]
          } else {
            next[notif.channel_id] = (prev[notif.channel_id] || 0) + 1
          }
          return next
        })
      } else if (notif?.sender_id) {
        // Don't increment if this DM is currently open (full page or chat widget)
        const openDM = location.pathname.match(/^\/dm\/([0-9a-f-]{36})/)
        const openDMId = openDM ? openDM[1] : null
        if (openDMId === notif.sender_id) return
        if (chatWidgets.some(w => w.type === 'dm' && String(w.id) === String(notif.sender_id))) return
        setDmUnread(prev => ({ ...prev, [notif.sender_id]: (prev[notif.sender_id] || 0) + 1 }))
      }
    }
    window.addEventListener('dm-notification', handler)
    return () => window.removeEventListener('dm-notification', handler)
  }, [location.pathname, chatWidgets])

  // Clear unread for a user when their DM is opened
  useEffect(() => {
    const match = location.pathname.match(/^\/dm\/([0-9a-f-]{36})/)
    if (match) {
      const uid = match[1]
      setDmUnread(prev => {
        if (!prev[uid]) return prev
        const next = { ...prev }
        delete next[uid]
        return next
      })
    }
  }, [location.pathname])

  // Clear unread for a channel when it's opened + mark as read on backend
  useEffect(() => {
    const match = location.pathname.match(/^\/channel\/([0-9a-f-]{36})/)
    if (match) {
      const chId = match[1]
      setChannelUnread(prev => {
        const next = { ...prev }
        delete next[chId]
        return next
      })
      // Mark as read on backend, then re-fetch unread counts to stay in sync
      fetch(`${API_URL}/api/group-channel/${chId}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      }).then(res => {
        if (!res.ok) return
        // Sync total unread count from backend
        if (onMessagesRead) onMessagesRead()
        // Re-fetch workspace channels to sync unread map with backend
        if (selectedWorkspace) {
          fetch(`${API_URL}/api/workspaces/${selectedWorkspace.id}/channels`, { headers: getAuthHeaders() })
            .then(r => r.json())
            .then(data => setChannelUnread(withoutOpenChannel(data.unread)))
            .catch(() => {})
        }
      }).catch(() => {})
    }
  }, [location.pathname, onMessagesRead, selectedWorkspace])

  // Clear a channel's unread badge when it's marked as read elsewhere
  // (e.g. inside a chat widget, which doesn't change the route).
  useEffect(() => {
    const handler = (e: Event) => {
      const chId = (e as CustomEvent).detail?.channel_id
      if (!chId) return
      setChannelUnread(prev => {
        if (!prev[chId]) return prev
        const next = { ...prev }
        delete next[chId]
        return next
      })
    }
    window.addEventListener('channel-read', handler)
    return () => window.removeEventListener('channel-read', handler)
  }, [])

  const selectWorkspace = (ws: Workspace) => {
    setSelectedWorkspace(ws)
    localStorage.setItem('atlas_ws_id', String(ws.id))
    window.dispatchEvent(new Event('workspace-changed'))
  }

  const handleCreateWorkspace = async (e: ReactSyntheticEvent) => {
    e.preventDefault()
    if (!newWsName.trim()) return
    try {
      const res = await fetch(`${API_URL}/api/workspaces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: newWsName }),
      })
      const data = await res.json()
      if (res.ok) {
        setNewWsName('')
        setShowNewWorkspace(false)
        // Refresh workspaces
        fetch(`${API_URL}/api/workspaces`, { headers: getAuthHeaders() })
          .then(r => r.json())
          .then(d => {
            const ws = d.workspaces || []
            setWorkspaces(ws)
            if (ws.length > 0) selectWorkspace(ws[ws.length - 1])
          })
      }
    } catch { /* ignore */ }
  }

  const handleCreateBoard = async (e: ReactSyntheticEvent) => {
    e.preventDefault()
    if (!newBoardName.trim() || !selectedWorkspace) return
    try {
      const res = await fetch(`${API_URL}/api/workspaces/${selectedWorkspace.id}/boards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: newBoardName }),
      })
      const data = await res.json()
      if (res.ok) {
        setNewBoardName('')
        setShowNewBoard(false)
        // Refresh boards
        fetch(`${API_URL}/api/workspaces/${selectedWorkspace.id}/boards`, { headers: getAuthHeaders() })
          .then(r => r.json())
          .then(d => setWsBoards(d.boards || []))
          .catch(() => {})
      }
    } catch { /* ignore */ }
  }

  const handleCreateChannel = async (e: ReactSyntheticEvent) => {
    e.preventDefault()
    if (!newChannelName.trim() || !selectedWorkspace) return
    try {
      const res = await fetch(`${API_URL}/api/workspaces/${selectedWorkspace.id}/channels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: newChannelName }),
      })
      const data = await res.json()
      if (res.ok) {
        setNewChannelName('')
        setShowNewChannel(false)
        // Refresh channels
        fetch(`${API_URL}/api/workspaces/${selectedWorkspace.id}/channels`, { headers: getAuthHeaders() })
          .then(r => r.json())
          .then(d => setWsChannels(d.channels || []))
          .catch(() => {})
      }
    } catch { /* ignore */ }
  }

  const go = (path: string) => {
    if (isMobile) setSidebarOpen(false)
    if (path === 'workboard') {
      const wsId = selectedWorkspace?.id || localStorage.getItem('atlas_ws_id')
      if (wsId) {
        const boardId = sessionStorage.getItem('atlas_board_id')
        const tab = sessionStorage.getItem('atlas_workboard_tab') || 'board'
        if (boardId) {
          navigate(`/workboard/${wsId}/${boardId}/${tab}`)
        } else {
          navigate(`/workboard/${wsId}/${tab}`)
        }
      }
      return
    }
    if (path.startsWith('workboard/')) {
      navigate(`/${path}`)
      return
    }
    navigate(`/${path}`)
  }
  const isActive = (id: string) => {
    const path = location.pathname.replace(/^\//, '')
    return path === id || path.startsWith(id + '/')
  }

  return (
    <div className="app-shell">
      {/* Mobile sidebar backdrop */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="brand">
            <img src="/atlas.webp" alt="Atlas" className="brand-icon-img" />
            <div className="brand-text">
              <div className="brand-name">Atlas</div>
            </div>
          </div>
          <button className="icon-btn" onClick={() => { const next = !sidebarOpen; setSidebarOpen(next); localStorage.setItem('atlas_sidebar_open', String(next)) }} aria-label="Toggle menu">
            <MenuIcon size={18} />
          </button>
          <TaskNotificationBell />
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section-title">
            <span>Workspaces</span>
            <button className="sidebar-mini-create-btn" onClick={() => setShowNewWorkspace(true)} title="Novo workspace">
              <PlusIcon size={14} />
            </button>
          </div>
          <div className="sidebar-ws-select" ref={wsDropdownRef}>
            <button
              type="button"
              className={`sidebar-ws-select-btn ${wsDropdownOpen ? 'open' : ''}`}
              onClick={() => setWsDropdownOpen(o => !o)}
              onContextMenu={e => {
                e.preventDefault()
                if (selectedWorkspace) setWsContextMenu({ ws: selectedWorkspace, x: e.clientX, y: e.clientY })
              }}
            >
              <span className="sidebar-ws-select-label">{selectedWorkspace?.name || 'Selecionar...'}</span>
              <ChevronDownIcon size={14} />
            </button>
            {wsDropdownOpen && (
              <div className="sidebar-ws-dropdown">
                {workspaces.map(ws => (
                  <button
                    key={ws.id}
                    type="button"
                    className={`sidebar-ws-option ${selectedWorkspace?.id === ws.id ? 'active' : ''}`}
                    onClick={() => { selectWorkspace(ws); setWsDropdownOpen(false) }}
                    onContextMenu={e => {
                      e.preventDefault()
                      setWsContextMenu({ ws, x: e.clientX, y: e.clientY })
                      setWsDropdownOpen(false)
                    }}
                  >
                    <span className="sidebar-ws-option-check">
                      {selectedWorkspace?.id === ws.id && <CheckIcon size={13} />}
                    </span>
                    <span>{ws.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="nav-group">
            {mainMenu.map(item => (
              <button
                key={item.id}
                className={`nav-item ${isActive(item.id) ? 'active' : ''}`}
                onClick={() => go(item.id)}
              >
                {item.icon}
                <span>{item.name}</span>
                {item.id === 'inbox-channels' && unreadChannels > 0 && (
                  <span className="nav-badge">{unreadChannels > 99 ? '99+' : unreadChannels}</span>
                )}
                {item.id === 'emails' && unreadEmails > 0 && (
                  <span className="nav-badge">{unreadEmails > 99 ? '99+' : unreadEmails}</span>
                )}
                {item.id === 'bms' && downAccountIds.filter(id => !seenDownIds.has(id)).length > 0 && (
                  <span className="nav-badge alert blink">{downAccountIds.filter(id => !seenDownIds.has(id)).length > 99 ? '99+' : downAccountIds.filter(id => !seenDownIds.has(id)).length}</span>
                )}
              </button>
            ))}
          </div>

          <div className="sidebar-section-title">
            <span>Chats com IA</span>
            <button className="sidebar-mini-create-btn" onClick={() => go('sessions')} title="Novo chat">
              <PlusIcon size={14} />
            </button>
          </div>

          {/* Workboards for selected workspace */}
          {selectedWorkspace && (
            <>
              <div className="sidebar-section-title">
                <span
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => {
                    const allIds = new Set(wsBoards.map(b => String(b.id)))
                    // Always select all and enter multi mode
                    setMultiBoardIds(allIds)
                    sessionStorage.setItem('atlas_multi_board_ids', JSON.stringify(Array.from(allIds)))
                    sessionStorage.setItem('atlas_board_id', 'multi')
                    const lastTab = sessionStorage.getItem('atlas_workboard_tab') || 'board'
                    navigate(`/workboard/${selectedWorkspace.id}/multi/${lastTab}`)
                    window.dispatchEvent(new Event('multi-board-ids-changed'))
                  }}
                  title="Selecionar todos os workboards"
                >
                  Workboards
                </span>
                <button className="sidebar-mini-create-btn" onClick={() => setShowNewBoard(true)} title="Novo workboard">
                  <PlusIcon size={14} />
                </button>
              </div>
              <div className="nav-group nested">
                {wsBoards.map(b => {
                  const isChecked = multiBoardIds.has(String(b.id))
                  const selectBoard = () => {
                    setMultiBoardIds(prev => {
                      const next = new Set(prev)
                      next.add(String(b.id))
                      const lastTab = sessionStorage.getItem('atlas_workboard_tab') || 'board'
                      if (next.size > 1) {
                        sessionStorage.setItem('atlas_multi_board_ids', JSON.stringify(Array.from(next)))
                        sessionStorage.setItem('atlas_board_id', 'multi')
                        navigate(`/workboard/${selectedWorkspace.id}/multi/${lastTab}`)
                        window.dispatchEvent(new Event('multi-board-ids-changed'))
                        // Scroll to the selected board's section
                        setTimeout(() => {
                          const el = document.getElementById(`multi-board-group-${b.id}`)
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }, 300)
                      } else {
                        sessionStorage.setItem('atlas_multi_board_ids', JSON.stringify(Array.from(next)))
                        sessionStorage.setItem('atlas_board_id', String(b.id))
                        navigate(`/workboard/${selectedWorkspace.id}/${b.id}/${lastTab}`)
                        window.dispatchEvent(new Event('multi-board-ids-changed'))
                      }
                      return next
                    })
                  }
                  const toggleBoard = (e: ReactMouseEvent) => {
                    e.stopPropagation()
                    setMultiBoardIds(prev => {
                      const next = new Set(prev)
                      if (next.has(String(b.id))) next.delete(String(b.id))
                      else next.add(String(b.id))
                      const lastTab = sessionStorage.getItem('atlas_workboard_tab') || 'board'
                      if (next.size > 1) {
                        sessionStorage.setItem('atlas_multi_board_ids', JSON.stringify(Array.from(next)))
                        sessionStorage.setItem('atlas_board_id', 'multi')
                        navigate(`/workboard/${selectedWorkspace.id}/multi/${lastTab}`)
                        window.dispatchEvent(new Event('multi-board-ids-changed'))
                      } else if (next.size === 1) {
                        const singleId = Array.from(next)[0]
                        sessionStorage.setItem('atlas_multi_board_ids', JSON.stringify(Array.from(next)))
                        sessionStorage.setItem('atlas_board_id', String(singleId))
                        navigate(`/workboard/${selectedWorkspace.id}/${singleId}/${lastTab}`)
                        window.dispatchEvent(new Event('multi-board-ids-changed'))
                      } else {
                        // None selected — show explicit empty state
                        sessionStorage.setItem('atlas_multi_board_ids', '[]')
                        sessionStorage.setItem('atlas_board_id', 'none')
                        navigate(`/workboard/${selectedWorkspace.id}/none/${lastTab}`)
                      }
                      return next
                    })
                  }
                  return (
                    <div
                      key={b.id}
                      className={`nav-item ${isChecked ? 'active' : ''}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', position: 'relative' }}
                      onClick={selectBoard}
                      onContextMenu={(e) => {
                        e.preventDefault()
                        setBoardContextMenu({ board: b, x: e.clientX, y: e.clientY })
                      }}
                    >
                      <span className="board-check" style={{ width: 14, height: 14, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={toggleBoard}>
                        {isChecked && <CheckIcon size={12} />}
                      </span>
                      <InboxIcon size={14} /> <span>{b.name}</span>
                    </div>
                  )
                })}
                {showNewBoard && (
                  <form onSubmit={handleCreateBoard} style={{ display: 'flex', gap: 4, padding: '0 8px' }}>
                    <input
                      autoFocus
                      type="text"
                      value={newBoardName}
                      onChange={e => setNewBoardName(e.target.value)}
                      placeholder="Nome do board"
                      className="ws-input"
                      style={{ flex: 1, fontSize: 12 }}
                      onBlur={() => { if (!newBoardName.trim()) setShowNewBoard(false) }}
                    />
                    <button type="submit" className="icon-btn" style={{ padding: 2 }}>
                      <CheckIcon size={14} />
                    </button>
                  </form>
                )}
              </div>
            </>
          )}

          {/* Dynamic channels for selected workspace */}
          {selectedWorkspace && (
            <>
              <div className="sidebar-section-title">
                <span>Canais</span>
                <button className="sidebar-mini-create-btn" onClick={() => setShowNewChannel(true)} title="Novo canal">
                  <PlusIcon size={14} />
                </button>
              </div>
              <div className="nav-group nested">
                {wsChannels.map(ch => {
                  const activeCall = channelCalls[ch.id]
                  const callParticipants = activeCall?.participants || []
                  return (
                  <div
                    key={ch.id}
                    className={`nav-item-row ${isActive(`channel/${ch.id}`) ? 'active' : ''}`}
                  >
                    <button
                      className="nav-item nav-item-main"
                      onClick={() => openChatWidget('channel', ch.id, ch.name)}
                      onContextMenu={(e) => {
                        e.preventDefault()
                        setChannelContextMenu({ channel: ch, x: e.clientX, y: e.clientY })
                      }}
                    >
                      <HashIcon size={14} /> <span>{ch.name}</span>
                      {channelUnread[ch.id] > 0 && <span className="dm-unread-badge">{channelUnread[ch.id]}</span>}
                      {activeCall && (
                        <span className="channel-call-avatars">
                          {callParticipants.slice(0, 3).map(uid => {
                            const u = dmUsers.find(u => u.id === uid) || (uid === user?.id ? user : null)
                            const av = u?.avatar_url ? getAvatarUrl(u) : null
                            return av
                              ? <img key={uid} src={av} className={`channel-call-avatar ${uid === user?.id ? 'self' : ''}`} alt="" />
                              : <span key={uid} className={`channel-call-avatar fallback ${uid === user?.id ? 'self' : ''}`}><UserIcon size={10} /></span>
                          })}
                          {callParticipants.length > 3 && (
                            <span className="channel-call-avatar-more">+{callParticipants.length - 3}</span>
                          )}
                        </span>
                      )}
                    </button>
                    <button
                      className="nav-item-mini-btn"
                      onClick={(e) => { e.stopPropagation(); closeChatWidget('channel', ch.id); go(`channel/${ch.id}`) }}
                      title="Abrir em tela cheia"
                    >
                      <ExternalLinkIcon size={12} />
                    </button>
                  </div>
                  )
                })}
                {showNewChannel && (
                  <form onSubmit={handleCreateChannel} style={{ display: 'flex', gap: 4, padding: '0 8px' }}>
                    <input
                      autoFocus
                      type="text"
                      value={newChannelName}
                      onChange={e => setNewChannelName(e.target.value)}
                      placeholder="Nome do canal"
                      className="ws-input"
                      style={{ flex: 1, fontSize: 12 }}
                      onBlur={() => { if (!newChannelName.trim()) setShowNewChannel(false) }}
                    />
                    <button type="submit" className="icon-btn" style={{ padding: 2 }}>
                      <CheckIcon size={14} />
                    </button>
                  </form>
                )}
              </div>
            </>
          )}

          <div className="sidebar-section-title">
            <span>Mensagens diretas</span>
            <button
              className="sidebar-mini-create-btn"
              onClick={() => setShowBroadcastModal(true)}
              title="Enviar mensagem para todos"
              disabled={dmUsers.length === 0}
            >
              <MegaphoneIcon size={14} />
            </button>
          </div>
          <div className="nav-group nested">
            {dmUsers.map(u => {
              const buyerInfo = topBuyersMap[(u.email || '').toLowerCase()]
              const trophyColors = ['#FFD700', '#C0C0C0', '#CD7F32']
              const showTrophy = buyerInfo && buyerInfo.position <= 3
              const profitColor: string | undefined = buyerInfo ? (buyerInfo.profit > 0 ? '#17b817' : buyerInfo.profit < 0 ? '#ff0000' : '#888') : undefined
              return (
              <div
                key={u.id}
                className={`nav-item-row ${isActive(`dm/${u.id}`) ? 'active' : ''}`}
              >
                <button
                  className="nav-item nav-item-main"
                  onClick={() => openChatWidget('dm', u.id, u.display_name || u.name || u.email, getAvatarUrl(u), u.presence_status)}
                  onContextMenu={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setContextMenu({ x: e.clientX, y: e.clientY, userId: u.id, name: u.display_name || u.name || u.email })
                  }}
                >
                  <div className="dm-avatar presence-avatar">
                    {getAvatarUrl(u) ? (
                      <img src={getAvatarUrl(u)} alt="" className="avatar-img" style={{ width: 20, height: 20, borderRadius: '50%' }} />
                    ) : (
                      <UserIcon size={12} />
                    )}
                    <PresenceDot status={u.presence_status || 'offline'} size={7} />
                  </div>
                  <div className="dm-nav-info">
                    <span className="dm-nav-name" style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto', minWidth: 0 }}>
                        {u.display_name || u.name || u.email}
                      </span>
                      {buyerInfo && buyerInfo.profit !== 0 && (
                        <span style={{
                          fontSize: '9px',
                          fontWeight: 700,
                          color: profitColor,
                          marginLeft: 'auto',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                        }}>
                          {buyerInfo.profit > 0 ? (
                            showTrophy && (
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                <path d="M6 2h12v2c0 3.31-2.69 6-6 6S6 7.31 6 4V2z" fill={trophyColors[buyerInfo.position - 1]}/>
                                <path d="M4 3c0 3.87 3.13 7 7 7v2c-4.42 0-8-3.58-8-8V3h1z" fill={trophyColors[buyerInfo.position - 1]} opacity="0.7"/>
                                <path d="M20 3c0 3.87-3.13 7-7 7v2c4.42 0 8-3.58 8-8V3h-1z" fill={trophyColors[buyerInfo.position - 1]} opacity="0.7"/>
                                <rect x="10" y="10" width="4" height="6" fill={trophyColors[buyerInfo.position - 1]}/>
                                <path d="M7 16h10v2H7z" fill={trophyColors[buyerInfo.position - 1]}/>
                                <path d="M5 18h14v3H5z" fill={trophyColors[buyerInfo.position - 1]}/>
                                <path d="M8 21h8l-1 2H9z" fill={trophyColors[buyerInfo.position - 1]} opacity="0.6"/>
                              </svg>
                            )
                          ) : (
                            <SkullIcon size={11} color={profitColor} />
                          )}
                          {buyerInfo.position}º {formatCompact(buyerInfo.profit)}
                        </span>
                      )}
                    </span>
                    {u.status_message && (
                      <span className="dm-nav-status">{u.status_message}</span>
                    )}
                  </div>
                  {dmUnread[u.id] > 0 && (
                    <span className="dm-unread-badge">{dmUnread[u.id]}</span>
                  )}
                  {mutedUsers?.has(u.id) && (
                    <span style={{ marginLeft: dmUnread[u.id] > 0 ? '4px' : 'auto', display: 'flex', alignItems: 'center' }}><BellOffIcon size={12} /></span>
                  )}
                </button>
                <button
                  className="nav-item-mini-btn"
                  onClick={(e) => { e.stopPropagation(); closeChatWidget('dm', u.id); go(`dm/${u.id}`) }}
                  title="Abrir em tela cheia"
                >
                  <ExternalLinkIcon size={12} />
                </button>
              </div>
              )
            })}
          </div>
        </nav>

        <div className="sidebar-footer">
          {(() => {
            const myBuyerInfo = topBuyersMap[(user?.email || '').toLowerCase()]
            const trophyColors = ['#FFD700', '#C0C0C0', '#CD7F32']
            const myShowTrophy = myBuyerInfo && myBuyerInfo.position <= 3
            const myProfitColor: string | undefined = myBuyerInfo ? (myBuyerInfo.profit > 0 ? '#17b817' : myBuyerInfo.profit < 0 ? '#ff0000' : '#888') : undefined
            return (
          <div className="user-row">
            <button className="user-profile-btn" onClick={() => setShowProfileModal(true)} title="Editar perfil">
              <div className="avatar small">
                {user?.avatar_url ? <img src={getAvatarUrl(user)} alt="" className="avatar-img" /> : <UserIcon size={14} />}
              </div>
              <div className="user-profile-info">
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto', minWidth: 0 }}>
                    {user?.display_name || user?.email || 'Mateus Mendes'}
                  </span>
                  {myBuyerInfo && myBuyerInfo.profit !== 0 && (
                    <span style={{
                      fontSize: '9px',
                      fontWeight: 700,
                      color: myProfitColor,
                      marginLeft: 'auto',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                    }}>
                      {myBuyerInfo.profit > 0 ? (
                        myShowTrophy && (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                            <path d="M6 2h12v2c0 3.31-2.69 6-6 6S6 7.31 6 4V2z" fill={trophyColors[myBuyerInfo.position - 1]}/>
                            <path d="M4 3c0 3.87 3.13 7 7 7v2c-4.42 0-8-3.58-8-8V3h1z" fill={trophyColors[myBuyerInfo.position - 1]} opacity="0.7"/>
                            <path d="M20 3c0 3.87-3.13 7-7 7v2c4.42 0 8-3.58 8-8V3h-1z" fill={trophyColors[myBuyerInfo.position - 1]} opacity="0.7"/>
                            <rect x="10" y="10" width="4" height="6" fill={trophyColors[myBuyerInfo.position - 1]}/>
                            <path d="M7 16h10v2H7z" fill={trophyColors[myBuyerInfo.position - 1]}/>
                            <path d="M5 18h14v3H5z" fill={trophyColors[myBuyerInfo.position - 1]}/>
                            <path d="M8 21h8l-1 2H9z" fill={trophyColors[myBuyerInfo.position - 1]} opacity="0.6"/>
                          </svg>
                        )
                      ) : (
                        <SkullIcon size={11} color={myProfitColor} />
                      )}
                      {myBuyerInfo.position}º {formatCompact(myBuyerInfo.profit)}
                    </span>
                  )}
                </span>
                {editingStatus ? (
                  <input
                    className="sidebar-user-status-edit"
                    type="text"
                    value={statusDraft}
                    autoFocus
                    maxLength={255}
                    placeholder="Digite sua frase..."
                    onChange={e => setStatusDraft(e.target.value)}
                    onBlur={() => saveStatusMessage(statusDraft)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); saveStatusMessage(statusDraft) }
                      if (e.key === 'Escape') setEditingStatus(false)
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <span
                    className="sidebar-user-status"
                    onClick={(e) => {
                      e.stopPropagation()
                      setStatusDraft(user?.status_message || '')
                      setEditingStatus(true)
                    }}
                    title="Clique para editar sua frase"
                  >
                    {user?.status_message || 'Definir frase de aviso...'}
                  </span>
                )}
              </div>
            </button>
            <button className="icon-btn" onClick={() => go('settings')} title="Configuração" style={{ marginLeft: 'auto' }}>
              <SettingsIcon size={16} />
            </button>
            <button className="icon-btn danger" onClick={handleLogout} title="Sair">
              <LogoutIcon size={16} />
            </button>
          </div>
            )
          })()}
        </div>
      </aside>

      {showProfileModal && (
        <ProfileModal
          user={user}
          updateUser={updateUser}
          onClose={() => setShowProfileModal(false)}
        />
      )}

      {showBroadcastModal && (
        <BroadcastModal
          recipients={dmUsers}
          onClose={() => setShowBroadcastModal(false)}
        />
      )}

      <div className="main-area">
        {/* Mobile floating menu button */}
        {isMobile && !sidebarOpen && (
          <button className="mobile-menu-fab" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <MenuIcon size={20} />
          </button>
        )}
        {topBar}
        <div className={`content-area ${agentPanel ? 'with-agent' : ''}`}>
          <main className="main-content">
            {children}
          </main>
          {agentPanel && (
            <aside className="agent-drawer">
              {agentPanel}
            </aside>
          )}
        </div>
      </div>

      {/* New Workspace Modal */}
      {showNewWorkspace && (
        <div className="modal-overlay" onClick={() => setShowNewWorkspace(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Criar Workspace</h3>
            <form onSubmit={handleCreateWorkspace}>
              <input
                type="text"
                placeholder="Nome do workspace"
                value={newWsName}
                onChange={e => setNewWsName(e.target.value)}
                autoFocus
                style={{ width: '100%', marginBottom: 12 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn-primary">Criar</button>
                <button type="button" className="btn-ghost" onClick={() => setShowNewWorkspace(false)}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Context menu for DM users (right-click) */}
      {contextMenu && (
        <>
          <div className="context-menu-overlay" onClick={() => setContextMenu(null)} />
          <div
            className="dm-context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <div className="dm-context-header">{contextMenu.name}</div>
            <button
              className="dm-context-item"
              onClick={() => {
                const u = dmUsers.find(x => x.id === contextMenu.userId)
                if (u) setViewUserProfile(u)
                setContextMenu(null)
              }}
            >
              <UserIcon size={16} /> Ver perfil
            </button>
            {mutedUsers?.has(contextMenu.userId) ? (
              <button
                className="dm-context-item"
                onClick={() => { unmuteUser?.(contextMenu.userId); setContextMenu(null) }}
              >
                <BellIcon size={16} /> Reativar notificações
              </button>
            ) : (
              <button
                className="dm-context-item"
                onClick={() => { muteUser?.(contextMenu.userId); setContextMenu(null) }}
              >
                <BellOffIcon size={16} /> Silenciar notificações
              </button>
            )}
          </div>
        </>
      )}

      {/* View user profile modal (from DM list right-click) */}
      {viewUserProfile && (
        <div className="modal-overlay" onClick={() => setViewUserProfile(null)}>
          <div className="modal-content dm-profile-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Perfil do usuário</h3>
              <button className="icon-btn" onClick={() => setViewUserProfile(null)} title="Fechar">
                <XIcon size={18} />
              </button>
            </div>
            <div className="dm-profile-body">
              <div className="dm-profile-avatar">
                {getAvatarUrl(viewUserProfile) ? (
                  <img src={getAvatarUrl(viewUserProfile)} alt="" className="dm-profile-avatar-img" />
                ) : (
                  <div className="dm-profile-avatar-fallback"><UserIcon size={48} /></div>
                )}
              </div>
              <h2 className="dm-profile-name">{viewUserProfile.display_name || viewUserProfile.name || 'Sem nome'}</h2>
              <p className="dm-profile-email">{viewUserProfile.email}</p>
              {viewUserProfile.status_message && (
                <p className="dm-profile-status-message" style={{ color: 'var(--text-muted)', fontStyle: 'italic', margin: '4px 0 12px', fontSize: 13 }}>
                  "{viewUserProfile.status_message}"
                </p>
              )}
              <div className="dm-profile-info">
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Nome</span>
                  <span className="dm-profile-info-value">{viewUserProfile.name || '-'}</span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Display</span>
                  <span className="dm-profile-info-value">{viewUserProfile.display_name || '-'}</span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Email</span>
                  <span className="dm-profile-info-value">{viewUserProfile.email || '-'}</span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">ID</span>
                  <span className="dm-profile-info-value">#{viewUserProfile.id}</span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Status</span>
                  <span className="dm-profile-info-value">
                    <PresenceDot status={viewUserProfile.presence_status || 'offline'} size={8} />
                    {' '}{viewUserProfile.presence_status || 'offline'}
                  </span>
                </div>
                <div className="dm-profile-info-row">
                  <span className="dm-profile-info-label">Função</span>
                  <span className="dm-profile-info-value">{viewUserProfile.role || '-'}</span>
                </div>
                {viewUserProfile.created_at && (
                  <div className="dm-profile-info-row">
                    <span className="dm-profile-info-label">Membro desde</span>
                    <span className="dm-profile-info-value">{new Date(viewUserProfile.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                  </div>
                )}
                {viewUserProfile.last_seen_at && (
                  <div className="dm-profile-info-row">
                    <span className="dm-profile-info-label">Último acesso</span>
                    <span className="dm-profile-info-value">{new Date(viewUserProfile.last_seen_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                {user?.role === 'admin' && viewUserProfile.id !== user?.id && (
                  <button
                    className="btn-ghost"
                    style={viewUserProfile.deactivated ? {} : { color: 'var(--danger, #f85149)', borderColor: 'var(--danger, #f85149)' }}
                    disabled={deactivatingUser}
                    title={viewUserProfile.deactivated ? 'Reativar usuário' : 'Desativar usuário (some de DMs e hierarquia, bloqueia acesso)'}
                    onClick={async () => {
                      setDeactivatingUser(true)
                      try {
                        const res = await fetch(`${API_URL}/api/admin/users/${viewUserProfile.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                          body: JSON.stringify({ deactivated: !viewUserProfile.deactivated }),
                        })
                        if (res.ok) {
                          const target = viewUserProfile
                          setViewUserProfile(null)
                          if (!target.deactivated) {
                            setDmUsers(prev => prev.filter(x => x.id !== target.id))
                          } else {
                            setDmUsers(prev => prev.some(x => x.id === target.id) ? prev : [...prev, { ...target, deactivated: false }])
                          }
                        }
                      } catch { /* ignore */ }
                      setDeactivatingUser(false)
                    }}
                  >
                    {viewUserProfile.deactivated ? <RefreshIcon size={14} /> : <XCircleIcon size={14} />}
                    {' '}{viewUserProfile.deactivated ? 'Reativar usuário' : 'Desativar usuário'}
                  </button>
                )}
                <button className="btn-primary" onClick={() => { openChatWidget('dm', viewUserProfile.id, viewUserProfile.display_name || viewUserProfile.name || viewUserProfile.email, getAvatarUrl(viewUserProfile), viewUserProfile.presence_status); setViewUserProfile(null) }}>
                  Abrir conversa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workspace context menu */}
      {wsContextMenu && (
        <>
          <div className="context-menu-overlay" onClick={() => setWsContextMenu(null)} />
          <div
            className="context-menu"
            style={{ top: wsContextMenu.y, left: wsContextMenu.x }}
          >
            <button
              className="dm-context-item"
              onClick={() => {
                setEditWsModal(wsContextMenu.ws)
                setWsContextMenu(null)
              }}
            >
              <SettingsIcon size={16} /> Editar workspace
            </button>
          </div>
        </>
      )}

      {/* Edit workspace modal */}
      {editWsModal && (
        <EditWorkspaceModal
          workspace={editWsModal}
          allUsers={dmUsers}
          onClose={() => setEditWsModal(null)}
          onUpdate={() => {
            // Refresh workspaces
            fetch(`${API_URL}/api/workspaces`, { headers: getAuthHeaders() })
              .then(r => r.json())
              .then(data => {
                const ws = data.workspaces || []
                setWorkspaces(ws)
                const updated = ws.find((w: Workspace) => w.id === editWsModal.id)
                if (updated) setSelectedWorkspace(updated)
              })
          }}
        />
      )}

      {/* Channel context menu */}
      {channelContextMenu && (
        <>
          <div className="context-menu-overlay" onClick={() => setChannelContextMenu(null)} />
          <div
            className="context-menu"
            style={{ top: channelContextMenu.y, left: channelContextMenu.x }}
          >
            <button
              className="dm-context-item"
              onClick={() => {
                const ch = channelContextMenu.channel
                setChannelContextMenu(null)
                const newName = prompt('Novo nome do canal:', ch.name)
                if (newName && newName.trim() && newName.trim() !== ch.name) {
                  fetch(`${API_URL}/api/workspaces/${selectedWorkspace!.id}/channels/${ch.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                    body: JSON.stringify({ name: newName.trim() }),
                  }).then(r => r.ok ? r.json() : Promise.reject()).then(() => {
                    setWsChannels(prev => prev.map(c => c.id === ch.id ? { ...c, name: newName.trim() } : c))
                  }).catch(() => {})
                }
              }}
            >
              <EditIcon size={16} /> Renomear
            </button>
            <button
              className="dm-context-item"
              onClick={() => {
                setEditChannelModal(channelContextMenu.channel)
                setChannelContextMenu(null)
              }}
            >
              <UsersIcon size={16} /> Editar membros
            </button>
            <button
              className="dm-context-item danger"
              onClick={() => {
                const ch = channelContextMenu.channel
                setChannelContextMenu(null)
                if (confirm(`Apagar o canal "${ch.name}"? Todas as mensagens serão removidas.`)) {
                  fetch(`${API_URL}/api/workspaces/${selectedWorkspace!.id}/channels/${ch.id}`, {
                    method: 'DELETE',
                    headers: getAuthHeaders(),
                  }).then(() => {
                    setWsChannels(prev => prev.filter(c => c.id !== ch.id))
                    if (location.pathname.includes(`channel/${ch.id}`)) {
                      navigate('/sessions')
                    }
                  }).catch(() => {})
                }
              }}
            >
              <TrashIcon size={16} /> Apagar canal
            </button>
          </div>
        </>
      )}

      {/* Edit channel members modal */}
      {editChannelModal && (
        <EditChannelModal
          channel={editChannelModal}
          onClose={() => setEditChannelModal(null)}
        />
      )}

      {/* Board context menu */}
      {boardContextMenu && (
        <>
          <div className="context-menu-overlay" onClick={() => setBoardContextMenu(null)} />
          <div
            className="context-menu"
            style={{ top: boardContextMenu.y, left: boardContextMenu.x }}
          >
            <button
              className="dm-context-item"
              onClick={() => {
                setEditBoardModal(boardContextMenu.board)
                setBoardContextMenu(null)
              }}
            >
              <EditIcon size={16} /> Editar nome
            </button>
            <button
              className="dm-context-item"
              onClick={() => {
                setEditBoardMembersModal(boardContextMenu.board)
                setBoardContextMenu(null)
              }}
            >
              <UsersIcon size={16} /> Gerenciar membros
            </button>
            <button
              className="dm-context-item danger"
              onClick={() => {
                const board = boardContextMenu.board
                setBoardContextMenu(null)
                if (confirm(`Apagar workboard "${board.name}"? As tarefas serão mantidas mas não aparecerão em nenhum board.`)) {
                  fetch(`${API_URL}/api/workspaces/${selectedWorkspace!.id}/boards/${board.id}`, {
                    method: 'DELETE',
                    headers: getAuthHeaders(),
                  }).then(() => {
                    setWsBoards(prev => prev.filter(b => b.id !== board.id))
                    if (location.pathname.includes(`/workboard/${selectedWorkspace!.id}/${board.id}`)) {
                      navigate('/sessions')
                    }
                  }).catch(() => {})
                }
              }}
            >
              <TrashIcon size={16} /> Apagar
            </button>
          </div>
        </>
      )}

      {/* Edit board modal */}
      {editBoardModal && (
        <EditBoardModal
          board={editBoardModal}
          workspaceId={selectedWorkspace?.id}
          onUpdate={(updatedBoard?: KanbanBoard) => {
            if (!updatedBoard) return
            setWsBoards(prev => prev.map(b => b.id === updatedBoard.id ? updatedBoard : b))
          }}
          onClose={() => setEditBoardModal(null)}
        />
      )}

      {editBoardMembersModal && (
        <EditBoardMembersModal
          board={editBoardMembersModal}
          workspaceId={selectedWorkspace?.id}
          onClose={() => setEditBoardMembersModal(null)}
        />
      )}

      <ChatWidgetManager
        widgets={chatWidgets}
        onClose={closeChatWidget}
        onExpand={(type, id) => {
          closeChatWidget(type, id)
          if (type === 'whatsapp') {
            go('inbox-channels')
          } else {
            go(`${type}/${id}`)
          }
        }}
      />
    </div>
  )
}

